import {
  BufferGeometry,
  Color,
  InstancedMesh,
  Matrix4,
  Vector3,
  sRGBEncoding,
} from "three";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useViewSettings } from "@/view-settings";
import { useCursor, useGLTF, useTexture } from "@react-three/drei";
import { usePlaybackMediator } from "@/contexts/playback-context";
import { opaqueObject } from "@legendapp/state";
import {
  cameraIds$,
  camerasMesh$,
  hiddenTracks$,
  visibleTracksSnapshots$,
} from "../state";
import { highlightLastDetections } from "../tracks/useHighlightCamera";
import { CameraLabel } from "./camera-label";
import { CameraCalibration } from "@/shared/api";
import toast from "react-hot-toast";

const color = new Color();
const labelOffset = new Vector3(0, 0.1, 0);

export function CameraInstances({
  cameras,
  updateControlTarget,
}: {
  cameras: CameraCalibration[];
  updateControlTarget: (vector: Vector3) => void;
}) {
  const mediator = usePlaybackMediator();
  const viewSettings = useViewSettings();
  const three = useThree();
  const ref = useRef<InstancedMesh | null>(null);
  const { nodes } = useGLTF("/carmeraFrustum-transformed.glb", true);
  const [cameraLabels, setCameraLabels] = useState<any[]>();
  const [hovered, setHovered] = useState(false);
  useCursor(hovered);

  const onClick = (e: ThreeEvent<MouseEvent>) => {
    if (e.instanceId === undefined) return;

    e.stopPropagation();

    const camera = cameras[e.instanceId];

    if (!camera) return toast.error("Couldn't find selected camera");

    const target = new Vector3(
      camera.look_at[0],
      camera.look_at[2],
      -camera.look_at[1]
    );

    three.camera.position.set(
      camera.location[0],
      camera.location[2],
      -camera.location[1]
    );

    three.camera.lookAt(target);
    updateControlTarget(target);

    setTimeout(() => {
      viewSettings.lockCamera.set(true);
      prevColor.current.setHex(0xffffff);
      color.setHex(0xffffff);
      mediator.toggleDetectionsMode(camera.camera_id);
    });
  };

  const onPointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();

    if (!ref.current || e.instanceId === undefined) return;

    setHovered(true);

    ref.current.getColorAt(e.instanceId, color);

    prevColor.current = new Color(color);

    ref.current.setColorAt(e.instanceId, color.setHex(0xff0000));
    ref.current.instanceColor!.needsUpdate = true;
  };

  const onPointerOut = (e: ThreeEvent<PointerEvent>) => {
    if (!ref.current || e.instanceId === undefined) return;

    if (prevColor.current) {
      setHovered(false);
      color.setHex(0xfffffff);
      ref.current.setColorAt(e.instanceId, prevColor.current);
      ref.current.instanceColor!.needsUpdate = true;
    }
  };

  const geometry = useMemo(() => {
    const geo: BufferGeometry = (nodes.Cube as any).geometry.clone();
    geo.computeVertexNormals();
    geo.scale(0.1, 0.1, 0.1);
    geo.rotateX(-Math.PI * 0.5);

    return geo;
  }, [nodes]);

  useLayoutEffect(() => {
    if (!cameras || !cameras.length || !ref.current) return;

    camerasMesh$.set(opaqueObject(ref.current));
    const cameraLabels: JSX.Element[] = [];

    for (let i = 0; i < cameras.length; i++) {
      const camera = cameras[i];
      const cameraId = camera.camera_id;
      const instanceId = i;

      cameraIds$.push(cameraId);
      const transform = new Matrix4();
      const position = new Vector3(
        camera.location[0],
        camera.location[2],
        -camera.location[1]
      );

      const lookAt = new Vector3(
        camera.look_at[0],
        camera.look_at[2],
        -camera.look_at[1]
      );

      ref.current.setColorAt(instanceId, color);
      transform.setPosition(position);
      transform.lookAt(position, lookAt, new Vector3(0, 1, 0));
      ref.current.setMatrixAt(instanceId, transform);

      cameraLabels.push(
        <CameraLabel
          key={cameras[i].camera_id}
          position={position.add(labelOffset)}
          cameraId={cameras[i].camera_id}
        />
      );
    }

    setCameraLabels(cameraLabels);
  }, [cameras]);

  useEffect(() => {
    if (!cameras) return;

    // Highlight cameras when switched back from detections to tracks mode
    highlightLastDetections(
      visibleTracksSnapshots$.peek(),
      hiddenTracks$.peek()
    );
  }, [cameras]);

  const prevColor = useRef<Color>(new Color());
  const texture = useTexture("/matcap-texture.png");

  texture.encoding = sRGBEncoding;

  return (
    <>
      {cameraLabels}

      <instancedMesh
        onPointerOver={onPointerOver}
        onClick={onClick}
        onPointerOut={onPointerOut}
        ref={ref}
        args={[geometry, undefined, cameras.length]}
      >
        <meshMatcapMaterial attach="material" color="white" matcap={texture} />
      </instancedMesh>
    </>
  );
}
